import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  API_BASE_URL, FOOTER_TABS, FOOTER_COLUMNS, FOOTER_BRAND_FIELDS,
  FOOTER_MESSAGES, getSubmitButtonLabel,
  getContentUpdateDescription, getSheetUrl, autoGenerateUrls,
  FOOTER_DUPLICATE_CHECK, findDuplicates, HEADING_SECTION_COLUMNS, HEADING_SECTION_OFFICE_COLUMNS,
} from "./constants/adminConstants";
import AdminLogoEditor from "./AdminLogoEditor";
import defaultLogoImg from "../../assets/PNG/Logo.png";
import defaultLogoGif from "../../assets/Gif/Logo.gif";
import { resolveIcon } from "../Main/Footer/FooterData";
import { compressImageFile, validateImageFile } from "./compressImage";
import { uploadToCloudinary } from "./uploadImage";
import "./css/adminStyles.css";

const ICON_PRESETS = ["whatsapp", "profile", "calendar", "phone", "contact"];
const SOCIAL_ICON_PRESETS = ["facebook", "instagram", "linkedin", "youtube", "twitter", "tiktok"];

// ── Scrollable table wrapper with left/right scroll buttons ────────────

function TableWithScrollButtons({ children, style }) {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      setShowLeft(el.scrollLeft > 5);
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll]);

  const scrollTo = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" });
  };

  return (
    <div className="AdminTableScrollWrap" style={style}>
      <div className="AdminScrollBar">
          <button className="AdminScrollBtn" onClick={() => scrollTo("left")} disabled={!showLeft} title="Scroll left">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="10 4 6 8 10 12" />
            </svg>
          </button>
          <button className="AdminScrollBtn" onClick={() => scrollTo("right")} disabled={!showRight} title="Scroll right">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 4 10 8 6 12" />
            </svg>
          </button>
        </div>
      <div ref={scrollRef} className="AdminTableScroll">
        {children}
      </div>
    </div>
  );
}
function AdminFooterEditor({ user, section, onBack }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("socials");
  const [tabOrder, setTabOrder] = useState(FOOTER_TABS.map(t => t.id));
  const dragRef = useRef(null);
  const tabBarRef = useRef(null);
  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    const wheelHandler = (e) => {
      if (e.deltaY !== 0) { e.preventDefault(); el.scrollLeft += e.deltaY; }
    };
    el.addEventListener("wheel", wheelHandler, { passive: false });
    return () => el.removeEventListener("wheel", wheelHandler);
  }, []);
  const [sectionInfo, setSectionInfo] = useState(null);
  const [logoEditorOpen, setLogoEditorOpen] = useState(false);
  const [gifEditorOpen, setGifEditorOpen] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successTitle, setSuccessTitle] = useState("");
  const [arraySearch, setArraySearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  const [headingSearch, setHeadingSearch] = useState("");
  const [headingSearchOpen, setHeadingSearchOpen] = useState(false);
  const headingSearchRef = useRef(null);
  useEffect(() => {
    if (headingSearchOpen && headingSearchRef.current) {
      headingSearchRef.current.focus();
    }
  }, [headingSearchOpen]);

  const [headingRowSearch, setHeadingRowSearch] = useState("");
  const [headingRowSearchOpen, setHeadingRowSearchOpen] = useState(false);
  const headingRowSearchRef = useRef(null);
  useEffect(() => {
    if (headingRowSearchOpen && headingRowSearchRef.current) {
      headingRowSearchRef.current.focus();
    }
  }, [headingRowSearchOpen]);

  useEffect(() => {
    setArraySearch("");
    setSearchOpen(false);
    setHeadingSearch("");
    setHeadingSearchOpen(false);
    setHeadingRowSearch("");
    setHeadingRowSearchOpen(false);
  }, [activeTab]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  // Row form modal state
  const [showRowForm, setShowRowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingItem, setEditingItem] = useState({});
  const [duplicateInfo, setDuplicateInfo] = useState([]);
  const [duplicateActionLoading, setDuplicateActionLoading] = useState(false);
  const fileInputRef = useRef(null);
  const headingFileInputRef = useRef(null);
  const snapshotRef = useRef(null);
  const [pendingIconUpload, setPendingIconUpload] = useState(null);
  const [pendingHeadingIconUpload, setPendingHeadingIconUpload] = useState(null);
  const [pendingInsertIndex, setPendingInsertIndex] = useState(null);
  const [showRowDetailForm, setShowRowDetailForm] = useState(false);
  const [rowDetailSectionIndex, setRowDetailSectionIndex] = useState(null);
  const [rowDetailInsertIndex, setRowDetailInsertIndex] = useState(null);
  const [rowDetailItem, setRowDetailItem] = useState({});

  const handleIconUploadClick = (field, index, key) => {
    setPendingIconUpload({ field, index, key });
    fileInputRef.current?.click();
  };

  const handleIconFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !pendingIconUpload) return;
    const check = validateImageFile(file);
    if (!check.valid) { alert(check.message); e.target.value = ""; return; }
    compressImageFile(file, 48, 0.8).then(async (dataUrl) => {
      try {
        const url = await uploadToCloudinary(dataUrl, file.name);
        handleArrayChange(pendingIconUpload.field, pendingIconUpload.index, pendingIconUpload.key, url);
      } catch (err) {
        alert("Upload failed: " + err.message);
      }
      setPendingIconUpload(null);
    });
    e.target.value = "";
  };

  const handleHeadingIconUploadClick = (sectionIndex, rowIndex, key) => {
    setPendingHeadingIconUpload({ sectionIndex, rowIndex, key });
    headingFileInputRef.current?.click();
  };

  const handleHeadingIconFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !pendingHeadingIconUpload) return;
    const check = validateImageFile(file);
    if (!check.valid) { alert(check.message); e.target.value = ""; return; }
    compressImageFile(file, 48, 0.8).then(async (dataUrl) => {
      try {
        const url = await uploadToCloudinary(dataUrl, file.name);
        handleHeadingSectionRowChange(pendingHeadingIconUpload.sectionIndex, pendingHeadingIconUpload.rowIndex, pendingHeadingIconUpload.key, url);
      } catch (err) {
        alert("Upload failed: " + err.message);
      }
      setPendingHeadingIconUpload(null);
    });
    e.target.value = "";
  };

  useEffect(() => {
    loadContent();
  }, []);

  const normalizeFooterContent = (raw) => {
    if (!raw) return raw;
    if (raw.headingSections) {
      const desiredOrder = ["Company", "Features", "Office"];
      const ordered = [];
      for (const name of desiredOrder) {
        const found = raw.headingSections.find(
          (s) => (s.heading || "").trim().toLowerCase() === name.toLowerCase()
        );
        if (found) ordered.push(found);
      }
      for (const s of raw.headingSections) {
        if (!ordered.includes(s)) ordered.push(s);
      }
      raw.headingSections = ordered;
      return raw;
    }
    const c = { ...raw };
    const sections = [];
    const h = c.headings || {};
    if (Array.isArray(c.companyLinks) && c.companyLinks.length > 0) {
      sections.push({
        heading: h.company || "Company",
        sectionType: "links",
        rows: c.companyLinks.map(l => ({ label: l.label || "", href: l.href || "", sectionId: l.sectionId || "", description: l.description || "" })),
      });
    }
    if (Array.isArray(c.featureLinks) && c.featureLinks.length > 0) {
      sections.push({
        heading: h.features || "Features",
        sectionType: "links",
        rows: c.featureLinks.map(l => ({ label: l.label || "", href: l.href || "", sectionId: l.sectionId || "", description: l.description || "" })),
      });
    }
    if (Array.isArray(c.officeDetails) && c.officeDetails.length > 0) {
      sections.push({
        heading: h.office || "Office",
        sectionType: "office",
        rows: c.officeDetails.map(d => ({ icon: d.icon || "", iconKind: d.iconKind || "", text: d.text || "" })),
      });
    }
    if (sections.length > 0) c.headingSections = sections;
    return c;
  };

  const loadContent = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data && data.section) {
        const normalized = normalizeFooterContent(data.section.content);
        setContent(normalized);
        snapshotRef.current = JSON.stringify(normalized);
        setSectionInfo({ version: data.section.version, updatedAt: data.section.updatedAt });
      } else {
        setMessage(FOOTER_MESSAGES.apiError);
      }
    } catch (err) {
      setMessage(FOOTER_MESSAGES.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSheet = () => {
    window.open(getSheetUrl(), "_blank", "noopener,noreferrer");
  };

  const handlePushToSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/footer/push-sheet`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) {
        const text = await res.text();
        console.error("Push response:", res.status, text);
        setMessage(`Push failed (${res.status}): ${text.substring(0, 200)}`);
        return;
      }
      const data = await res.json();
      setMessage(data.success ? "Pushed to Google Sheet successfully!" : data.error || "Push failed.");
    } catch (err) {
      console.error("Push error:", err);
      setMessage("Push error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePullFromSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/footer/pull-sheet`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (data.success) {
        setContent(data.content);
        setMessage("Pulled from Google Sheet. Review changes and click Publish to go live.");
      } else {
        setMessage(data.error || "Pull failed.");
      }
    } catch (err) {
      setMessage("Pull error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleArrayChange = (field, index, key, value) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...updated[field]];
      arr[index] = { ...arr[index], [key]: value };
      updated[field] = arr;
      return updated;
    });
  };

  const handleIconPresetSelect = (field, index, key, kindKey, preset) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...updated[field]];
      arr[index] = { ...arr[index], [key]: preset, [kindKey]: preset };
      updated[field] = arr;
      return updated;
    });
  };

  const handleAddRowBefore = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...updated[field]];
      const emptyRow = {};
      for (const key of Object.keys(arr[0] || {})) {
        emptyRow[key] = "";
      }
      arr.splice(index, 0, emptyRow);
      updated[field] = arr;
      return updated;
    });
  };

  const handleAddRowAfter = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...updated[field]];
      const emptyRow = {};
      for (const key of Object.keys(arr[0] || {})) {
        emptyRow[key] = "";
      }
      arr.splice(index + 1, 0, emptyRow);
      updated[field] = arr;
      return updated;
    });
  };

  const handleDeleteRow = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      updated[field] = updated[field].filter((_, i) => i !== index);
      return updated;
    });
  };

  const handleMoveRowUp = (field, index) => {
    if (index === 0) return;
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...updated[field]];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleMoveRowDown = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...updated[field]];
      if (index >= arr.length - 1) return prev;
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleFieldChange = (field, value) => {
    setContent((prev) => {
      if (field.startsWith("brand.") || field.startsWith("headings.")) {
        const parts = field.split(".");
        return {
          ...prev,
          [parts[0]]: { ...prev[parts[0]], [parts[1]]: value },
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleHeadingSectionRowChange = (sectionIndex, rowIndex, key, value) => {
    setContent((prev) => {
      const sections = [...(prev.headingSections || [])];
      const rows = [...(sections[sectionIndex].rows || [])];
      rows[rowIndex] = { ...rows[rowIndex], [key]: value };
      sections[sectionIndex] = { ...sections[sectionIndex], rows };
      return { ...prev, headingSections: sections };
    });
  };

  const handleDeleteHeadingSection = (index) => {
    setContent((prev) => {
      const sections = [...(prev.headingSections || [])];
      sections.splice(index, 1);
      return { ...prev, headingSections: sections };
    });
  };

  const handleMoveHeadingSectionUp = (index) => {
    if (index === 0) return;
    setContent((prev) => {
      const sections = [...(prev.headingSections || [])];
      [sections[index - 1], sections[index]] = [sections[index], sections[index - 1]];
      return { ...prev, headingSections: sections };
    });
  };

  const handleMoveHeadingSectionDown = (index) => {
    setContent((prev) => {
      const sections = [...(prev.headingSections || [])];
      if (index >= sections.length - 1) return prev;
      [sections[index], sections[index + 1]] = [sections[index + 1], sections[index]];
      return { ...prev, headingSections: sections };
    });
  };

  const handleAddHeadingSectionRow = (sectionIndex) => {
    setContent((prev) => {
      const sections = [...(prev.headingSections || [])];
      const isOffice = sections[sectionIndex]?.sectionType === "office";
      const emptyRow = isOffice
        ? { icon: "", iconKind: "", text: "" }
        : { label: "", href: "", sectionId: "", description: "" };
      const rows = [...(sections[sectionIndex].rows || []), emptyRow];
      sections[sectionIndex] = { ...sections[sectionIndex], rows };
      return { ...prev, headingSections: sections };
    });
  };

  const handleDeleteHeadingSectionRow = (sectionIndex, rowIndex) => {
    setContent((prev) => {
      const sections = [...(prev.headingSections || [])];
      const rows = sections[sectionIndex].rows.filter((_, i) => i !== rowIndex);
      sections[sectionIndex] = { ...sections[sectionIndex], rows };
      return { ...prev, headingSections: sections };
    });
  };

  const handleMoveHeadingSectionRowUp = (sectionIndex, rowIndex) => {
    if (rowIndex === 0) return;
    setContent((prev) => {
      const sections = [...(prev.headingSections || [])];
      const rows = [...(sections[sectionIndex].rows || [])];
      [rows[rowIndex - 1], rows[rowIndex]] = [rows[rowIndex], rows[rowIndex - 1]];
      sections[sectionIndex] = { ...sections[sectionIndex], rows };
      return { ...prev, headingSections: sections };
    });
  };

  const handleMoveHeadingSectionRowDown = (sectionIndex, rowIndex) => {
    setContent((prev) => {
      const sections = [...(prev.headingSections || [])];
      const rows = [...(sections[sectionIndex].rows || [])];
      if (rowIndex >= rows.length - 1) return prev;
      [rows[rowIndex], rows[rowIndex + 1]] = [rows[rowIndex + 1], rows[rowIndex]];
      sections[sectionIndex] = { ...sections[sectionIndex], rows };
      return { ...prev, headingSections: sections };
    });
  };

  // ── Row Form Modal handlers ─────────────────────────────────────────

  const handleOpenAddHeadingForm = () => {
    setEditingIndex(null);
    setEditingItem({ heading: "", sectionType: "links" });
    setShowRowForm(true);
  };

  const handleOpenEditHeadingForm = (index) => {
    const item = content?.headingSections?.[index];
    if (!item) return;
    setEditingIndex(index);
    setEditingItem({ heading: item.heading || "", sectionType: item.sectionType || "links" });
    setShowRowForm(true);
  };

  const handleCloseRowForm = () => {
    setShowRowForm(false);
    setEditingIndex(null);
    setEditingItem({});
    setPendingInsertIndex(null);
  };

  const handleRowFormFieldChange = (key, value) => {
    setEditingItem((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveRowForm = () => {
    const heading = (editingItem.heading || "").trim();
    if (!heading) return;
    const sectionType = editingItem.sectionType || "links";
    const emptyRow = sectionType === "office"
      ? { icon: "", iconKind: "", text: "" }
      : { label: "", href: "", sectionId: "", description: "" };

    setContent((prev) => {
      const sections = [...(prev.headingSections || [])];
      if (editingIndex === null) {
        const insertIdx = pendingInsertIndex !== null ? pendingInsertIndex : sections.length;
        sections.splice(insertIdx, 0, { heading, sectionType, rows: [] });
      } else {
        const existing = sections[editingIndex];
        const oldType = existing.sectionType;
        const newType = sectionType;
        let rows = existing.rows || [];
        if (oldType !== newType) {
          rows = rows.map(r => newType === "office"
            ? { icon: "", iconKind: "", text: r.text || "" }
            : { label: r.label || "", href: r.href || "", sectionId: r.sectionId || "", description: r.description || "" }
          );
        }
        sections[editingIndex] = { ...existing, heading, sectionType, rows };
      }
      return { ...prev, headingSections: sections };
    });
    handleCloseRowForm();
  };

  const handleOpenAddHeadingFormBefore = (index) => {
    setPendingInsertIndex(index);
    setEditingIndex(null);
    setEditingItem({ heading: "", sectionType: "links" });
    setShowRowForm(true);
  };

  const handleOpenAddHeadingFormAfter = (index) => {
    setPendingInsertIndex(index + 1);
    setEditingIndex(null);
    setEditingItem({ heading: "", sectionType: "links" });
    setShowRowForm(true);
  };

  const handleOpenAddHeadingRowForm = (sectionIndex, insertIndex) => {
    const section = content?.headingSections?.[sectionIndex];
    if (!section) return;
    const isOffice = section.sectionType === "office";
    const emptyItem = isOffice
      ? { icon: "", iconKind: "", text: "" }
      : { label: "", href: "", sectionId: "", description: "" };
    setRowDetailSectionIndex(sectionIndex);
    setRowDetailInsertIndex(insertIndex);
    setRowDetailItem(emptyItem);
    setShowRowDetailForm(true);
  };

  const handleRowDetailFieldChange = (key, value) => {
    setRowDetailItem((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveRowDetailForm = () => {
    setContent((prev) => {
      const sections = [...(prev.headingSections || [])];
      const section = { ...sections[rowDetailSectionIndex] };
      const rows = [...(section.rows || [])];
      rows.splice(rowDetailInsertIndex, 0, { ...rowDetailItem });
      section.rows = rows;
      sections[rowDetailSectionIndex] = section;
      return { ...prev, headingSections: sections };
    });
    handleCloseRowDetailForm();
  };

  const handleCloseRowDetailForm = () => {
    setShowRowDetailForm(false);
    setRowDetailSectionIndex(null);
    setRowDetailInsertIndex(null);
    setRowDetailItem({});
  };

  const logoHistory = content?.brand?.logoHistory || [];
  const logoBin = content?.brand?.logoBin || [];
  const gifHistory = content?.brand?.gifHistory || [];
  const gifBin = content?.brand?.gifBin || [];

  const handleDeleteHistory = (index) => {
    setContent((prev) => {
      const history = [...(prev?.brand?.logoHistory || [])];
      const bin = [...(prev?.brand?.logoBin || [])];
      const deleted = history.splice(index, 1);
      if (deleted.length) bin.push(deleted[0]);
      return { ...prev, brand: { ...prev?.brand, logoHistory: history, logoBin: bin } };
    });
  };

  const handleRestoreFromBin = (index) => {
    setContent((prev) => {
      const bin = [...(prev?.brand?.logoBin || [])];
      const history = [...(prev?.brand?.logoHistory || [])];
      const restored = bin.splice(index, 1);
      if (restored.length) history.unshift(restored[0]);
      return { ...prev, brand: { ...prev?.brand, logoHistory: history, logoBin: bin } };
    });
  };

  const handleFooterBrandImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "image/png") { alert("Only PNG files are allowed."); return; }
    const check = validateImageFile(file);
    if (!check.valid) { alert(check.message); return; }
    try {
      const dataUrl = await compressImageFile(file, 200, 0.85);
      const finalUrl = dataUrl.startsWith("data:") ? await uploadToCloudinary(dataUrl, "brand-logo") : dataUrl;
      setContent((prev) => {
        const currentLogo = prev?.brand?.logoImage;
        const history = prev?.brand?.logoHistory || [];
        const newHistory = currentLogo && currentLogo !== finalUrl
          ? [currentLogo, ...history.filter((h) => h !== currentLogo && h !== finalUrl)].slice(0, 20)
          : history;
        return { ...prev, brand: { ...prev?.brand, logoImage: finalUrl, logoHistory: newHistory } };
      });
    } catch (err) { alert("Upload failed: " + err.message); }
    e.target.value = "";
  };

  const handleFooterBrandGifUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "image/gif") { alert("Only GIF files are allowed."); return; }
    const check = validateImageFile(file);
    if (!check.valid) { alert(check.message); return; }
    try {
      const dataUrl = await new Promise((resolve) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.readAsDataURL(file); });
      const finalUrl = dataUrl.startsWith("data:") ? await uploadToCloudinary(dataUrl, "brand-gif") : dataUrl;
      setContent((prev) => {
        const currentGif = prev?.brand?.logoGif;
        const history = prev?.brand?.gifHistory || [];
        const newHistory = currentGif && currentGif !== finalUrl
          ? [currentGif, ...history.filter((h) => h !== currentGif && h !== finalUrl)].slice(0, 20)
          : history;
        return { ...prev, brand: { ...prev?.brand, logoGif: finalUrl, gifHistory: newHistory } };
      });
    } catch (err) { alert("Upload failed: " + err.message); }
    e.target.value = "";
  };

  const handleFooterLogoEditorSave = async (dataUrl) => {
    let finalUrl = dataUrl;
    if (dataUrl && dataUrl.startsWith("data:")) {
      try { finalUrl = await uploadToCloudinary(dataUrl, "brand-logo"); }
      catch (err) { alert("Upload failed: " + err.message); return; }
    }
    setContent((prev) => {
      const currentLogo = prev?.brand?.logoImage;
      const history = prev?.brand?.logoHistory || [];
      const newHistory = currentLogo && currentLogo !== finalUrl
        ? [currentLogo, ...history.filter((h) => h !== currentLogo && h !== finalUrl)].slice(0, 20)
        : history;
      return { ...prev, brand: { ...prev?.brand, logoImage: finalUrl, logoHistory: newHistory } };
    });
    setLogoEditorOpen(false);
  };

  const handleFooterGifEditorSave = async (dataUrl) => {
    let finalUrl = dataUrl;
    if (dataUrl && dataUrl.startsWith("data:")) {
      try { finalUrl = await uploadToCloudinary(dataUrl, "brand-gif"); }
      catch (err) { alert("Upload failed: " + err.message); return; }
    }
    setContent((prev) => {
      const currentGif = prev?.brand?.logoGif;
      const history = prev?.brand?.gifHistory || [];
      const newHistory = currentGif && currentGif !== finalUrl
        ? [currentGif, ...history.filter((h) => h !== currentGif && h !== finalUrl)].slice(0, 20)
        : history;
      return { ...prev, brand: { ...prev?.brand, logoGif: finalUrl, gifHistory: newHistory } };
    });
    setGifEditorOpen(false);
  };

  const handleGifDeleteHistory = (index) => {
    setContent((prev) => {
      const history = [...(prev?.brand?.gifHistory || [])];
      const bin = [...(prev?.brand?.gifBin || [])];
      const deleted = history.splice(index, 1);
      if (deleted.length) bin.push(deleted[0]);
      return { ...prev, brand: { ...prev?.brand, gifHistory: history, gifBin: bin } };
    });
  };

  const handleGifRestoreFromBin = (index) => {
    setContent((prev) => {
      const bin = [...(prev?.brand?.gifBin || [])];
      const history = [...(prev?.brand?.gifHistory || [])];
      const restored = bin.splice(index, 1);
      if (restored.length) history.unshift(restored[0]);
      return { ...prev, brand: { ...prev?.brand, gifHistory: history, gifBin: bin } };
    });
  };

  const handleSubmit = async () => {
    const dupes = findDuplicates(content, FOOTER_DUPLICATE_CHECK);
    if (dupes.length > 0) {
      setDuplicateInfo(dupes);
      setShowDuplicateModal(true);
      return;
    }
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleConfirmSubmit = async () => {
    if (!noteText.trim()) return;
    setShowNoteModal(false);
    await doSubmit(noteText.trim());
  };

  const handleContinueAnyway = async () => {
    setShowDuplicateModal(false);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleRemoveDuplicates = async () => {
    setDuplicateActionLoading(true);
    const cleanContent = JSON.parse(JSON.stringify(content));
    const sorted = [...duplicateInfo].sort((a, b) => {
      if (a.sectionIndex !== undefined && b.sectionIndex !== undefined) {
        if (a.sectionIndex !== b.sectionIndex) return b.sectionIndex - a.sectionIndex;
      }
      return b.duplicateIndex - a.duplicateIndex;
    });
    for (const dup of sorted) {
      if (dup.nestedField) {
        const sections = cleanContent[dup.field] || [];
        const section = sections[dup.sectionIndex];
        if (!section) continue;
        const rows = [...(section[dup.nestedField] || [])];
        if (rows[dup.duplicateIndex]) {
          rows.splice(dup.duplicateIndex, 1);
          section[dup.nestedField] = rows;
        }
      } else if (dup.parent) {
        if (!cleanContent[dup.parent]) continue;
        const arr = [...(cleanContent[dup.parent][dup.field] || [])];
        if (arr[dup.duplicateIndex]) {
          arr.splice(dup.duplicateIndex, 1);
          cleanContent[dup.parent] = { ...cleanContent[dup.parent], [dup.field]: arr };
        }
      } else {
        const arr = [...(cleanContent[dup.field] || [])];
        if (arr[dup.duplicateIndex]) {
          arr.splice(dup.duplicateIndex, 1);
          cleanContent[dup.field] = arr;
        }
      }
    }
    setContent(cleanContent);
    setShowDuplicateModal(false);
    setDuplicateActionLoading(false);
    snapshotRef.current = JSON.stringify(cleanContent);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit("", cleanContent);
  };

  const doSubmit = async (submitterNote, overrideContent) => {
    setMessage("");
    setSaving(true);

    const token = sessionStorage.getItem("tvc_token");

    const submitPayload = overrideContent || { ...content };
    if (submitPayload.brand) {
      submitPayload.brand = { ...submitPayload.brand };
      delete submitPayload.brand.logoHistory;
      delete submitPayload.brand.logoBin;
      delete submitPayload.brand.gifHistory;
      delete submitPayload.brand.gifBin;
    }
    if (submitPayload.headingSections) {
      delete submitPayload.headings;
      delete submitPayload.companyLinks;
      delete submitPayload.featureLinks;
      delete submitPayload.officeDetails;
    }

    const estimateSize = new Blob([JSON.stringify(submitPayload)]).size;
    if (estimateSize > 4 * 1024 * 1024) {
      setMessage(`Payload too large (${(estimateSize / 1024 / 1024).toFixed(1)} MB). Reduce image sizes before submitting.`);
      setSaving(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/content/submit-change`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          description: getContentUpdateDescription(section.label),
          changes: {
            type: "content_update",
            sectionKey: section.key,
            content: submitPayload,
            ...(submitterNote ? { submitterNote } : {}),
          },
          sectionKey: section.key,
        }),
      });

      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { error: text }; }
      if (res.ok) {
        snapshotRef.current = JSON.stringify(content);
        const role = user?.role || "Staff";
        setSuccessTitle(role === "Admin" ? "Changes Published" : "Request made");
        setShowSuccessOverlay(true);
      } else {
        console.error("Submit failed:", res.status, text);
        setMessage(data.error || `Server error (${res.status})`);
      }
    } catch (err) {
      setMessage(`Network error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const renderArrayEditor = (field, columns) => {
    const items = content?.[field] || [];
    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">
            {field.replace(/([A-Z])/g, " $1").trim()}
          </h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={searchRef}
                type="text"
                className="AdminPillSearch"
                placeholder="Search..."
                value={arraySearch}
                onChange={(e) => setArraySearch(e.target.value)}
                onBlur={() => { setArraySearch(""); setTimeout(() => setSearchOpen(false), 200) }}
              />
              {!searchOpen && (
                <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
              )}
            </div>
            <button
              className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
              onClick={() => handleAddRowAfter(field, items.length - 1)}
            >
              + Add Row
            </button>
          </div>
        </div>
        <div className="AdminTableScroll">
          <table className="AdminTable AdminTableEditor">
            <thead>
              <tr>
                <th className="AdminThOrder">Order</th>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th className="AdminThActions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items
                .map((item, idx) => ({ item, idx }))
                .filter(({ item }) => {
                  if (!arraySearch.trim()) return true;
                  return columns.some((col) =>
                    String(item[col.key] || "").toLowerCase().includes(arraySearch.trim().toLowerCase())
                  );
                })
                .map(({ item, idx }) => (
                <tr
                  key={idx}
                  draggable="true"
                  onDragStart={(e) => {
                    dragRef.current = { type: "array", field, fromIndex: idx };
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                    e.currentTarget.style.opacity = "0.4";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    e.currentTarget.style.outline = "2px dashed #3b82f6";
                    e.currentTarget.style.outlineOffset = "-2px";
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.style.outline = "";
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    if (!dragRef.current || dragRef.current.type !== "array") { dragRef.current = null; return; }
                    const { field: fromField, fromIndex } = dragRef.current;
                    if (fromField !== field || fromIndex === idx) { dragRef.current = null; return; }
                    setContent((prev) => {
                      const updated = { ...prev };
                      const arr = [...(updated[field] || [])];
                      const [moved] = arr.splice(fromIndex, 1);
                      arr.splice(idx, 0, moved);
                      updated[field] = arr;
                      return updated;
                    });
                    dragRef.current = null;
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    dragRef.current = null;
                  }}
                >
                  <td>
                    <div className="AdminOrderBtns">
                      <button
                        className="AdminMoveBtn"
                        onClick={() => handleMoveRowUp(field, idx)}
                        disabled={idx === 0}
                        title="Move up"
                      >
                        &#8593;
                      </button>
                      <button
                        className="AdminMoveBtn"
                        onClick={() => handleMoveRowDown(field, idx)}
                        disabled={idx === items.length - 1}
                        title="Move down"
                      >
                        &#8595;
                      </button>
                    </div>
                  </td>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.type === "icon" ? (
                        <div className="AdminIconCell">
                          <span className="AdminIconPreview">
                            {resolveIcon(item[col.key])}
                          </span>
                          <input
                            className="AdminInput AdminTableInput"
                            value={col.kindKey ? (item[col.kindKey] || "") : (item[col.key] === "" || /^[a-zA-Z]+$/.test(item[col.key]) ? item[col.key] : "image")}
                            readOnly={!col.kindKey && item[col.key] !== "" && !/^[a-zA-Z]+$/.test(item[col.key] || "")}
                            onChange={(e) => handleArrayChange(field, idx, col.kindKey || col.key, e.target.value)}
                            placeholder={col.label}
                          />
                          {field === "socials" ? (
                            <select
                              className="AdminInput AdminTableInput"
                              style={{ minWidth: 90, maxWidth: 120 }}
                              value=""
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v) handleIconPresetSelect(field, idx, col.key, col.kindKey, v);
                              }}
                            >
                              <option value="">Presets...</option>
                              {SOCIAL_ICON_PRESETS.map((p) => (
                                <option key={p} value={p}>{p}</option>
                              ))}
                            </select>
                          ) : null}
                          <button className="AdminIconUploadBtn" title="Upload icon image" onClick={() => handleIconUploadClick(field, idx, col.key)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/>
                            </svg>
                          </button>
                          {item[col.key] && (
                            <button
                              className="AdminIconBtn AdminIconBtnRed"
                              title="Clear"
                              onClick={() => handleArrayChange(field, idx, col.key, "")}
                              style={{ width: 24, height: 24, fontSize: 12, marginLeft: 2 }}
                            >
                              &#10005;
                            </button>
                          )}
                        </div>
                      ) : (
                        <input
                          className="AdminInput AdminTableInput"
                          value={item[col.key] || ""}
                          onChange={(e) => handleArrayChange(field, idx, col.key, e.target.value)}
                          placeholder={col.label}
                        />
                      )}
                    </td>
                  ))}
                  <td>
                    <div className="AdminActionBtns">
                      <button
                        className="AdminIconBtn AdminIconBtnBlue"
                        onClick={() => handleAddRowBefore(field, idx)}
                        title="Add row before"
                      >
                        &#9650;+
                      </button>
                      <button
                        className="AdminIconBtn AdminIconBtnBlue"
                        onClick={() => handleAddRowAfter(field, idx)}
                        title="Add row after"
                      >
                        &#9660;+
                      </button>
                      <button
                        className="AdminIconBtn AdminIconBtnRed"
                        onClick={() => handleDeleteRow(field, idx)}
                        title="Delete row"
                      >
                        &#10005;
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {items.length === 0 && (
          <div className="AdminEmptyState">{FOOTER_MESSAGES.emptyArray}</div>
        )}
        {items.length > 0 && arraySearch.trim() && !items.some((item) =>
          columns.some((col) =>
            String(item[col.key] || "").toLowerCase().includes(arraySearch.trim().toLowerCase())
          )
        ) && (
          <div className="AdminEmptyState">No results found for "{arraySearch}"</div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="AdminEmptyState">{FOOTER_MESSAGES.loading}</div>;
  }

  if (!content) {
    return (
      <div>
        <button className="AdminButton AdminButtonSmall AdminButtonSecondary AdminBackBtn" onClick={onBack}>
          &larr; Back to Content Manager
        </button>
        <div className="AdminEmptyState">{FOOTER_MESSAGES.noContent}</div>
      </div>
    );
  }

  const isDirty = JSON.stringify(content) !== snapshotRef.current;
  const orderedTabs = [...FOOTER_TABS].sort((a, b) => tabOrder.indexOf(a.id) - tabOrder.indexOf(b.id));

  return (
    <div className="AdminCard">
      <input type="file" accept="image/gif" ref={fileInputRef} style={{ display: "none" }} onChange={handleIconFileChange} />
      <input type="file" accept="image/png,image/gif,image/jpeg,image/webp" ref={headingFileInputRef} style={{ display: "none" }} onChange={handleHeadingIconFileChange} />
      <div className="AdminCardHeader">
        <h3 className="AdminFooterH3">
          Edit Footer Content
          {(() => {
            const k = activeTab === "headings" ? "headingSections" : activeTab;
            return k !== "data" && Array.isArray(content?.[k]) ? <span className="AdminCountBadge">{content[k].length}</span> : null;
          })()}
        </h3>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
          <button className="AdminButton AdminButtonSmall AdminSubmitBtn" onClick={handleSubmit} disabled={!isDirty || saving} style={{ whiteSpace: "nowrap" }}>
            {saving ? "Publishing..." : getSubmitButtonLabel(user?.role)}
          </button>
          <button
            className="AdminButton AdminButtonSmall AdminSheetBtn"
            onClick={handleOpenSheet}
          >
            Google Sheet
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePushToSheet} disabled={loading} title="Push to Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePullFromSheet} disabled={loading} title="Pull from Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
        </div>
      </div>

      {message && (
        <p className={`AdminMsgBox ${message.includes("success") ? "AdminSuccess" : "AdminError"}`}>
          {message}
        </p>
      )}

      <div className="AdminTabBar" ref={tabBarRef} style={{ display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0", marginBottom: 16 }}>
        {orderedTabs.map((tab) => (
          <button
            key={tab.id}
            draggable
            onDragStart={(e) => { dragRef.current = tab.id; e.target.style.opacity = '0.5'; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const fromId = dragRef.current;
              if (!fromId || fromId === tab.id) return;
              const newOrder = [...tabOrder];
              const fromIdx = newOrder.indexOf(fromId);
              const toIdx = newOrder.indexOf(tab.id);
              newOrder.splice(fromIdx, 1);
              newOrder.splice(toIdx, 0, fromId);
              setTabOrder(newOrder);
            }}
            onDragEnd={(e) => { e.target.style.opacity = '1'; dragRef.current = null; }}
            className={`AdminTabBtn ${activeTab === tab.id ? "AdminTabBtnActive" : ""}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 20px", border: "none", background: activeTab === tab.id ? "#1B2A4A" : "transparent",
              color: activeTab === tab.id ? "#fff" : "#64748b", cursor: "grab", fontSize: 14, fontWeight: 600,
              borderRadius: "8px 8px 0 0", transition: "all 0.2s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "data" && (
        <div className="AdminCard">
          <div className="AdminDataHeader">
            <h4 className="AdminSectionTitle">
              Data
            </h4>
          </div>
          <div className="AdminDataInfo">
            <div>
              <span className="AdminDataLabel">Last Updated</span>
              <p className="AdminDataValue">
                {sectionInfo?.updatedAt ? new Date(sectionInfo.updatedAt).toLocaleString() : "Not published"}
              </p>
            </div>
            <div>
              <span className="AdminDataLabel">Section Key</span>
              <p className="AdminDataValue">
                {section?.key || "footer"}
              </p>
            </div>
          </div>
          <textarea
            className="AdminJsonEditor"
            value={JSON.stringify(content, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                setContent(parsed);
                setMessage("");
              } catch {
                setMessage(FOOTER_MESSAGES.invalidJson);
              }
            }}
          />
        </div>
      )}

      {activeTab === "socials" && renderArrayEditor("socials", FOOTER_COLUMNS.socials)}

      {activeTab === "brand" && (
        <div className="AdminCard">
          <div className="AdminSectionHeader">
            <h4 className="AdminSectionTitle">
              Brand Information
            </h4>
          </div>
          <div className="AdminBrandGrid">
          {FOOTER_BRAND_FIELDS.map((f) => {
            if (f.key === "logoImage") {
              return (
                <div className="AdminFormGroup" key={f.key}>
                  <label className="AdminLabel">Logo Image</label>
                  {content?.brand?.logoImage ? (
                    <div className="AdminLogoPreview" onClick={() => setLogoEditorOpen(true)} style={{ cursor: "pointer" }}>
                      <img src={content?.brand?.logoImage || defaultLogoImg} alt="Footer Logo" className="AdminLogoPreviewImg" />
                    </div>
                  ) : (
                    <label className="AdminLogoPreview" style={{ cursor: "pointer" }}>
                      <img src={defaultLogoImg} alt="Footer Logo" className="AdminLogoPreviewImg" />
                      <input type="file" accept="image/png" onChange={handleFooterBrandImageUpload} style={{ display: "none" }} />
                    </label>
                  )}
                </div>
              );
            }
            if (f.key === "logoGif") {
              return (
                <div className="AdminFormGroup" key={f.key}>
                  <label className="AdminLabel">Logo GIF</label>
                  {content?.brand?.logoGif ? (
                    <div className="AdminLogoPreview" onClick={() => setGifEditorOpen(true)} style={{ cursor: "pointer" }}>
                      <img src={content.brand.logoGif} alt="Footer GIF" className="AdminLogoPreviewImg" style={{ objectFit: "contain" }} />
                    </div>
                  ) : (
                    <label className="AdminLogoPreview" style={{ cursor: "pointer" }}>
                      <div className="AdminLogoPreviewPlaceholder">Click to upload GIF</div>
                      <input type="file" accept="image/gif" onChange={handleFooterBrandGifUpload} style={{ display: "none" }} />
                    </label>
                  )}
                </div>
              );
            }
            return f.type === "textarea" ? (
              <div className="AdminFormGroup" key={f.key}>
                <label className="AdminLabel">{f.label}</label>
                <textarea
                  className="AdminTextarea"
                  value={content?.brand?.[f.key] || ""}
                  onChange={(e) => handleFieldChange(`brand.${f.key}`, e.target.value)}
                  rows={3}
                />
              </div>
            ) : (
              <div className="AdminFormGroup" key={f.key}>
                <label className="AdminLabel">{f.label}</label>
                <input
                  className="AdminInput"
                  value={content?.brand?.[f.key] || ""}
                  onChange={(e) => handleFieldChange(`brand.${f.key}`, e.target.value)}
                />
              </div>
            );
          })}
          </div>
        </div>
      )}

      {logoEditorOpen && (
        <AdminLogoEditor
          currentImage={content?.brand?.logoImage || defaultLogoImg}
          logoHistory={logoHistory}
          logoBin={logoBin}
          onSave={handleFooterLogoEditorSave}
          onDeleteHistory={handleDeleteHistory}
          onRestoreFromBin={handleRestoreFromBin}
          onPermanentDelete={(index) => {
            setContent((prev) => {
              const bin = [...(prev?.brand?.logoBin || [])];
              bin.splice(index, 1);
              return { ...prev, brand: { ...prev?.brand, logoBin: bin } };
            });
          }}
          onClose={() => setLogoEditorOpen(false)}
          accept="image/png"
        />
      )}

      {gifEditorOpen && (
        <AdminLogoEditor
          currentImage={content?.brand?.logoGif || defaultLogoGif}
          logoHistory={gifHistory}
          logoBin={gifBin}
          onSave={handleFooterGifEditorSave}
          onDeleteHistory={handleGifDeleteHistory}
          onRestoreFromBin={handleGifRestoreFromBin}
          onPermanentDelete={(index) => {
            setContent((prev) => {
              const bin = [...(prev?.brand?.gifBin || [])];
              bin.splice(index, 1);
              return { ...prev, brand: { ...prev?.brand, gifBin: bin } };
            });
          }}
          onClose={() => setGifEditorOpen(false)}
          accept="image/gif"
        />
      )}

      {activeTab === "headings" && (
        <>
          {/* Groups table — same as Resources table in VisaDetailEditor */}
          <div className="AdminCardMb">
            <div className="AdminSectionHeader">
              <h4 className="AdminSectionTitle">Section Headings</h4>
              <div className="AdminFlexRowGap8 AdminFlexShrink0">
                <div className={`AdminPillSearchWrap ${headingSearchOpen ? 'is-open' : ''}`}>
                  <span className="AdminPillSearchIcon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                  </span>
                  <input
                    ref={headingSearchRef}
                    type="text"
                    className="AdminPillSearch"
                    placeholder="Search..."
                    value={headingSearch}
                    onChange={(e) => setHeadingSearch(e.target.value)}
                     onBlur={() => { setHeadingSearch(""); setTimeout(() => setHeadingSearchOpen(false), 200) }}
                  />
                  {!headingSearchOpen && (
                    <button className="AdminPillSearchOverlay" onClick={() => setHeadingSearchOpen(true)} type="button" aria-label="Search" />
                  )}
                </div>
                <button
                  className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
                  onClick={handleOpenAddHeadingForm}
                >
                  + Add Section
                </button>
              </div>
            </div>
            <TableWithScrollButtons>
              <table className="AdminTable AdminTableEditor">
                <thead>
                  <tr>
                    <th className="AdminThOrder">Order</th>
                    <th>Heading</th>
                    <th>Type</th>
                    <th className="AdminThActions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(content?.headingSections || [])
                    .map((section, sIdx) => ({ section, sIdx }))
                    .filter(({ section }) => {
                      if (!headingSearch.trim()) return true;
                      const q = headingSearch.trim().toLowerCase();
                      return (section.heading || "").toLowerCase().includes(q) || (section.sectionType || "").toLowerCase().includes(q);
                    })
                    .map(({ section, sIdx }) => (
                    <tr key={sIdx}>
                      <td>
                        <div className="AdminOrderBtns">
                          <button
                            className="AdminMoveBtn"
                            onClick={() => handleMoveHeadingSectionUp(sIdx)}
                            disabled={sIdx === 0}
                            title="Move up"
                          >&#8593;</button>
                          <button
                            className="AdminMoveBtn"
                            onClick={() => handleMoveHeadingSectionDown(sIdx)}
                            disabled={sIdx === (content?.headingSections || []).length - 1}
                            title="Move down"
                          >&#8595;</button>
                          <button
                            className="AdminEditBtn"
                            onClick={() => handleOpenEditHeadingForm(sIdx)}
                            title="Edit section"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '0 4px', color: '#3b82f6' }}
                          >&#9998;</button>
                        </div>
                      </td>
                      <td>
                        <span style={{ fontSize: 13, color: '#333' }}>{section.heading || "—"}</span>
                      </td>
                      <td>
                        <span style={{ fontSize: 13, color: '#333' }}>{section.sectionType === "office" ? "Office" : "Links"}</span>
                      </td>
                      <td>
                        <div className="AdminActionBtns">
                          <button
                            className="AdminIconBtn AdminIconBtnBlue"
                            onClick={() => handleOpenAddHeadingFormBefore(sIdx)}
                            title="Add section before"
                          >&#9650;+</button>
                          <button
                            className="AdminIconBtn AdminIconBtnBlue"
                            onClick={() => handleOpenAddHeadingFormAfter(sIdx)}
                            title="Add section after"
                          >&#9660;+</button>
                          <button
                            className="AdminIconBtn AdminIconBtnRed"
                            onClick={() => handleDeleteHeadingSection(sIdx)}
                            title="Delete section and all its rows"
                          >&#10005;</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWithScrollButtons>
            {(content?.headingSections || []).length === 0 && !headingSearch.trim() && (
              <div className="AdminEmptyState">No sections. Click "Add Section" to create one.</div>
            )}
            {headingSearch.trim() && (content?.headingSections || []).filter((s) => (s.heading||"").toLowerCase().includes(headingSearch.trim().toLowerCase()) || (s.sectionType||"").toLowerCase().includes(headingSearch.trim().toLowerCase())).length === 0 && (
              <div className="AdminEmptyState">No sections found for "{headingSearch}"</div>
            )}
          </div>
          {/* Cards below for managing rows within each section */}
          {(content?.headingSections || []).map((section, sIdx) => {
            const isOffice = section.sectionType === "office";
            const columns = isOffice ? HEADING_SECTION_OFFICE_COLUMNS : HEADING_SECTION_COLUMNS;
            return (
              <div key={sIdx} className="AdminCardMb" style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 16 }}>
                <div className="AdminSectionHeader">
                  <h4 className="AdminSectionTitle">
                    <span style={{ fontSize: 16, fontWeight: 'bold', color: '#1B2A4A' }}>{section.heading || "Section"}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>({isOffice ? "Office" : "Links"})</span>
                  </h4>
                  <div className="AdminFlexRowGap8 AdminFlexShrink0">
                    <div className={`AdminPillSearchWrap ${headingRowSearchOpen ? 'is-open' : ''}`}>
                      <span className="AdminPillSearchIcon">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                      </span>
                      <input
                        ref={headingRowSearchRef}
                        type="text"
                        className="AdminPillSearch"
                        placeholder="Search..."
                        value={headingRowSearch}
                        onChange={(e) => setHeadingRowSearch(e.target.value)}
                         onBlur={() => { setHeadingRowSearch(""); setTimeout(() => setHeadingRowSearchOpen(false), 200) }}
                      />
                      {!headingRowSearchOpen && (
                        <button className="AdminPillSearchOverlay" onClick={() => setHeadingRowSearchOpen(true)} type="button" aria-label="Search" />
                      )}
                    </div>
                    <button
                      className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
                      onClick={() => handleAddHeadingSectionRow(sIdx)}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      + Add {isOffice ? "Row" : "Link"}
                    </button>
                  </div>
                </div>
                <TableWithScrollButtons>
                  <table className="AdminTable AdminTableEditor">
                    <thead>
                      <tr>
                        <th className="AdminThOrder">Order</th>
                        {columns.map((col) => (
                          <th key={col.key}>{col.label}</th>
                        ))}
                        <th className="AdminThActions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(section.rows || [])
                        .map((row, rIdx) => ({ row, rIdx }))
                        .filter(({ row }) => {
                          if (!headingRowSearch.trim()) return true;
                          const q = headingRowSearch.trim().toLowerCase();
                          return columns.some((col) => String(row[col.key] || "").toLowerCase().includes(q));
                        })
                        .map(({ row, rIdx }) => (
                        <tr key={rIdx}>
                          <td>
                            <div className="AdminOrderBtns">
                              <button className="AdminMoveBtn" onClick={() => handleMoveHeadingSectionRowUp(sIdx, rIdx)} disabled={rIdx === 0} title="Move up">&#8593;</button>
                              <button className="AdminMoveBtn" onClick={() => handleMoveHeadingSectionRowDown(sIdx, rIdx)} disabled={rIdx === (section.rows || []).length - 1} title="Move down">&#8595;</button>
                            </div>
                          </td>
                          {columns.map((col) => (
                            <td key={col.key}>
                              {col.type === "icon" ? (
                                <div className="AdminIconCell">
                                  <span className="AdminIconPreview">{resolveIcon(row[col.key])}</span>
                                  <input className="AdminInput AdminTableInput" value={row[col.kindKey || col.key] || ""} onChange={(e) => handleHeadingSectionRowChange(sIdx, rIdx, col.kindKey || col.key, e.target.value)} placeholder={col.label} />
                                  <select className="AdminInput AdminTableInput" style={{ minWidth: 90, maxWidth: 110 }} value="" onChange={(e) => { const v = e.target.value; if (v) { handleHeadingSectionRowChange(sIdx, rIdx, col.key, v); handleHeadingSectionRowChange(sIdx, rIdx, col.kindKey, v); }}}>
                                    <option value="">Presets...</option>
                                    {ICON_PRESETS.map((p) => (<option key={p} value={p}>{p}</option>))}
                                  </select>
                                  <button className="AdminIconUploadBtn" title="Upload icon image" onClick={() => handleHeadingIconUploadClick(sIdx, rIdx, col.key)}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/></svg>
                                  </button>
                                  {row[col.key] && (
                                    <button className="AdminIconBtn AdminIconBtnRed" title="Clear" onClick={() => { handleHeadingSectionRowChange(sIdx, rIdx, col.key, ""); handleHeadingSectionRowChange(sIdx, rIdx, col.kindKey, ""); }} style={{ width: 24, height: 24, fontSize: 12, marginLeft: 2 }}>&#10005;</button>
                                  )}
                                </div>
                              ) : (
                                <input className="AdminInput AdminTableInput" value={row[col.key] || ""} onChange={(e) => handleHeadingSectionRowChange(sIdx, rIdx, col.key, e.target.value)} placeholder={col.label} />
                              )}
                            </td>
                          ))}
                          <td>
                            <div className="AdminActionBtns">
                              <button
                                className="AdminIconBtn AdminIconBtnBlue"
                                onClick={() => handleOpenAddHeadingRowForm(sIdx, rIdx)}
                                title="Add row before"
                              >&#9650;+</button>
                              <button
                                className="AdminIconBtn AdminIconBtnBlue"
                                onClick={() => handleOpenAddHeadingRowForm(sIdx, rIdx + 1)}
                                title="Add row after"
                              >&#9660;+</button>
                              <button className="AdminIconBtn AdminIconBtnRed" onClick={() => handleDeleteHeadingSectionRow(sIdx, rIdx)} title="Delete row">&#10005;</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableWithScrollButtons>
                {(section.rows || []).length === 0 && !headingRowSearch.trim() && (
                  <div style={{ fontSize: 13, color: "#94a3b8", padding: "8px 0" }}>No {isOffice ? "rows" : "links"}. Click "Add {isOffice ? "Row" : "Link"}" to create one.</div>
                )}
                {headingRowSearch.trim() && (section.rows || []).filter((r) => columns.some((col) => String(r[col.key] || "").toLowerCase().includes(headingRowSearch.trim().toLowerCase()))).length === 0 && (
                  <div style={{ fontSize: 13, color: "#94a3b8", padding: "8px 0" }}>No {isOffice ? "rows" : "links"} found for "{headingRowSearch}"</div>
                )}
              </div>
            );
          })}
        </>
      )}

      {showRowForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: 28, borderRadius: 10, minWidth: 420, maxWidth: 520, maxHeight: '85vh', overflowY: 'auto', position: 'relative' }}>
            <button
              onClick={handleCloseRowForm}
              style={{
                position: 'absolute', top: 12, right: 12,
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 20, color: '#94a3b8', padding: '4px 8px',
                lineHeight: 1, borderRadius: 4,
              }}
              title="Close"
              onMouseOver={(e) => e.currentTarget.style.color = '#1B2A4A'}
              onMouseOut={(e) => e.currentTarget.style.color = '#94a3b8'}
            >
              &#10005;
            </button>
            <h3 style={{ margin: '0 0 4px 0', fontSize: 20, textAlign: 'center' }}>
              {editingIndex === null ? 'Add' : 'Edit'} Heading Section
            </h3>
            <p style={{ margin: '12px 0 20px 0', fontSize: 13, color: '#888', textAlign: 'center' }}>
              Fill in the details and press Continue to add it to the table.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                  Heading Name
                </label>
                <input
                  className="AdminInput"
                  style={{ width: '100%', fontSize: 13, padding: '6px 10px' }}
                  value={editingItem.heading || ""}
                  onChange={(e) => handleRowFormFieldChange("heading", e.target.value)}
                  placeholder="e.g. Company"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                  Section Type
                </label>
                <select
                  className="AdminInput"
                  style={{ width: '100%', fontSize: 13, padding: '6px 10px' }}
                  value={editingItem.sectionType || "links"}
                  onChange={(e) => handleRowFormFieldChange("sectionType", e.target.value)}
                >
                  <option value="links">Links (Label, URL, Section ID, Description)</option>
                  <option value="office">Office (Icon, Text)</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 24 }}>
              <button
                className="AdminButton AdminButtonSmall"
                onClick={handleSaveRowForm}
                disabled={!editingItem.heading?.trim()}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showRowDetailForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: 28, borderRadius: 10, minWidth: 420, maxWidth: 520, maxHeight: '85vh', overflowY: 'auto', position: 'relative' }}>
            <button
              onClick={handleCloseRowDetailForm}
              style={{
                position: 'absolute', top: 12, right: 12,
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 20, color: '#94a3b8', padding: '4px 8px',
                lineHeight: 1, borderRadius: 4,
              }}
              title="Close"
              onMouseOver={(e) => e.currentTarget.style.color = '#1B2A4A'}
              onMouseOut={(e) => e.currentTarget.style.color = '#94a3b8'}
            >
              &#10005;
            </button>
            <h3 style={{ margin: '0 0 4px 0', fontSize: 20, textAlign: 'center' }}>
              Add Row Details
            </h3>
            <p style={{ margin: '12px 0 20px 0', fontSize: 13, color: '#888', textAlign: 'center' }}>
              Fill in the fields and press Continue to add the row.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
              {(() => {
                const section = content?.headingSections?.[rowDetailSectionIndex];
                const isOffice = section?.sectionType === "office";
                const fields = isOffice
                  ? [
                      { key: "icon", label: "Icon", type: "icon", kindKey: "iconKind" },
                      { key: "text", label: "Text" },
                    ]
                  : [
                      { key: "label", label: "Label" },
                      { key: "href", label: "URL" },
                      { key: "sectionId", label: "Section ID" },
                      { key: "description", label: "Description" },
                    ];
                return fields.map((f) => (
                  <div key={f.key}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                      {f.label}
                    </label>
                    {f.type === "icon" ? (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span className="AdminIconPreview">{resolveIcon(rowDetailItem[f.key])}</span>
                        <input
                          className="AdminInput"
                          style={{ flex: 1, fontSize: 13, padding: '6px 10px' }}
                          value={rowDetailItem[f.kindKey || f.key] || ""}
                          onChange={(e) => {
                            handleRowDetailFieldChange(f.key, e.target.value);
                            if (f.kindKey) handleRowDetailFieldChange(f.kindKey, e.target.value);
                          }}
                          placeholder={f.label}
                        />
                        <select
                          className="AdminInput"
                          style={{ width: 100, fontSize: 13, padding: '6px 6px' }}
                          value=""
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v) {
                              handleRowDetailFieldChange(f.key, v);
                              if (f.kindKey) handleRowDetailFieldChange(f.kindKey, v);
                            }
                          }}
                        >
                          <option value="">Presets...</option>
                          {ICON_PRESETS.map((p) => (<option key={p} value={p}>{p}</option>))}
                        </select>
                      </div>
                    ) : (
                      <input
                        className="AdminInput"
                        style={{ width: '100%', fontSize: 13, padding: '6px 10px' }}
                        value={rowDetailItem[f.key] || ""}
                        onChange={(e) => handleRowDetailFieldChange(f.key, e.target.value)}
                        placeholder={f.label}
                      />
                    )}
                  </div>
                ));
              })()}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 24 }}>
              <button
                className="AdminButton AdminButtonSmall"
                onClick={handleSaveRowDetailForm}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showNoteModal && (
        <div className="AdminModal">
          <div className="AdminModalContent" style={{ maxWidth: 480 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 className="AdminModalTitle" style={{ margin: 0 }}>Describe Your Changes</h3>
              <button className="AdminCloseBtn" onClick={() => setShowNoteModal(false)}>&times;</button>
            </div>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 12px 0" }}>
              Please provide a note describing what changes you made. This is required before submitting.
            </p>
            <textarea
              className="AdminTextarea"
              style={{ minHeight: 100, width: "100%", boxSizing: "border-box" }}
              placeholder="Describe what changed..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              autoFocus
            />
            <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
              <button
                className="AdminButton AdminSubmitBtn"
                onClick={handleConfirmSubmit}
                disabled={!noteText.trim() || saving}
                style={{ opacity: !noteText.trim() ? 0.5 : 1 }}
              >
                {saving ? "Submitting..." : "Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessOverlay && (
        <div className="AdminModal" onClick={() => setShowSuccessOverlay(false)}>
          <div className="AdminModalContent" style={{ maxWidth: 400, textAlign: "center" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>
              {successTitle === "Changes Published" ? "\u2705" : "\uD83D\uDCEB"}
            </div>
            <h3 className="AdminModalTitle" style={{ marginBottom: 4 }}>{successTitle}</h3>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 20px 0" }}>
              {successTitle === "Changes Published" ? "Your content changes have been published live." : "Your change request has been submitted for approval."}
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                className="AdminButton AdminSubmitBtn"
                onClick={() => setShowSuccessOverlay(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateModal && duplicateInfo.length > 0 && (
        <div className="AdminModal">
          <div className="AdminModalContent" style={{ maxWidth: 520, position: "relative" }}>
            <button onClick={() => setShowDuplicateModal(false)} style={{ position: "absolute", top: 8, right: 12, background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#64748b", lineHeight: 1 }} title="Close">&times;</button>
            <div style={{ fontSize: 36, textAlign: "center", marginBottom: 8 }}>&#9888;</div>
            <h3 className="AdminModalTitle" style={{ textAlign: "center", margin: "0 0 4px 0" }}>Duplicate Entries Detected</h3>
            <p style={{ fontSize: 13, color: "#64748b", textAlign: "center", margin: "0 0 16px 0" }}>
              The following duplicate entries were found. Choose how to proceed.
            </p>
            <div style={{ background: "#fff8f0", border: "1px solid #fecaca", borderRadius: 8, padding: 12, marginBottom: 16 }}>
              {(() => {
                const byTab = {};
                for (const d of duplicateInfo) {
                  if (!byTab[d.tabId]) byTab[d.tabId] = { label: d.tabLabel, items: [] };
                  const prefix = d.sectionHeading ? `[${d.sectionHeading}] ` : "";
                  byTab[d.tabId].items.push(prefix + d.duplicateValue);
                }
                return Object.entries(byTab).map(([tabId, info]) => (
                  <div key={tabId} style={{ marginBottom: 8, fontSize: 14, textAlign: "center" }}>
                    <strong style={{ color: "#1B2A4A" }}>{info.label}</strong>
                    <span style={{ color: "#475569" }}> &mdash; Duplicate{info.items.length > 1 ? "s" : ""}: </span>
                    <span style={{ color: "#1B2A4A" }}>{info.items.join(", ")}</span>
                  </div>
                ));
              })()}
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                className="AdminButton AdminSubmitBtn"
                onClick={handleRemoveDuplicates}
                disabled={duplicateActionLoading}
                style={{ background: "#dc2626", borderColor: "#dc2626" }}
              >
                {duplicateActionLoading ? "Removing..." : "Remove Duplicate Rows"}
              </button>
              <button
                className="AdminButton AdminSubmitBtn"
                onClick={handleContinueAnyway}
                disabled={duplicateActionLoading}
              >
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminFooterEditor;
